import { Button } from "@instacover/ui"
import cuid2 from "@paralleldrive/cuid2"
import { createFileRoute } from "@tanstack/react-router"
import { useCallback, useMemo } from "react"
import { Trans } from "react-i18next"

import Map, { GeoJsonObj } from "@/components/Map"
import MapSkeleton from "@/components/MapSkeleton"
import { WallGraphic } from "@/components/wall/WallGraphic"
import { db } from "@/database/database"
import { usePhotosByType, usePhotosByWall } from "@/database/hooks"
import { useSession } from "@/helpers/SessionProvider"
import PhotoDetailLayout from "@/sections/layouts/PhotoDetailLayout"
import { pickGps } from "@/utils/gps"
import { logger } from "@/utils/logging"

export const Route = createFileRoute("/$sessionId/_session/_steps/addNewWall")({
  component: RouteComponent,
})

function RouteComponent() {
  const navigate = Route.useNavigate()
  const { session } = useSession()

  const onAddAnotherWall = useCallback(async () => {
    const newWallNumber = session.numberOfWalls + 1
    logger.info(`Adding another wall and navigating to it. New wall number: ${newWallNumber}`)

    navigate({
      to: "/$sessionId/wall/$wallNumber/$wallId",
      params: { wallId: cuid2.createId(), wallNumber: newWallNumber.toString(), sessionId: session.id },
      replace: true,
    })
  }, [navigate, session.id, session.numberOfWalls])

  const onNoOtherWalls = useCallback(async () => {
    const nextStep = await db.orderedStep.finishAndGetNext({ sessionId: session.id, step: "Wall" })

    if (nextStep) {
      navigate({ to: nextStep.to, params: { ...nextStep.params } })
    }
  }, [navigate, session.id])

  const { data: photos, isLoading: isLoadingPhotos } = usePhotosByWall({
    sessionId: session.id,
    wallNumber: session.numberOfWalls,
  })

  const { data: wallPhotos, isLoading: isLoadingWallPhotos } = usePhotosByType({
    sessionId: session.id,
    type: "Wall",
  })

  const lastWallPhotoGeoJsons = useMemo<GeoJsonObj[]>(() => {
    const photo = photos?.[0]
    const geoJsons = []

    if (photo?.buildingPolygonGeoJson) {
      geoJsons.push(photo.buildingPolygonGeoJson)
    }
    if (photo?.photoCoordinatesGeoJson) {
      geoJsons.push(photo.photoCoordinatesGeoJson)
    }
    if (photo?.visibleWallsGeoJson) {
      geoJsons.push(photo.visibleWallsGeoJson)
    }
    return geoJsons
  }, [photos])

  // The map is built from the last wall photo's geojsons, which already carry a marker for every
  // previous wall. A single photo without GPS would render it with markers missing, so the graphic
  // is shown instead.
  const hasEveryWallPhotoGps = useMemo(() => wallPhotos?.every((photo) => pickGps(photo)) ?? false, [wallPhotos])

  const isLoading = isLoadingPhotos || isLoadingWallPhotos
  const isMapShown = hasEveryWallPhotoGps && lastWallPhotoGeoJsons.length > 0

  return (
    <PhotoDetailLayout
      aboveHeading={
        <>
          {isLoading && <MapSkeleton />}

          {!isLoading && isMapShown && (
            <Map sessionId={session.id} geoJsons={lastWallPhotoGeoJsons} zoomControl={true} />
          )}

          {/* TODO placeholder  */}
          {!isLoading && !isMapShown && <WallGraphic type="NextWall" />}
        </>
      }
      heading={{
        i18nKey: "addNewWall.heading",
      }}
      subheading={{
        i18nKey: "general.howTo",
      }}
      description={{
        i18nKey: "addNewWall.description",
      }}
      buttons={
        <div className="mb-4 flex gap-4">
          <Button type="button" variant="primary" onClick={onNoOtherWalls} data-testid="add-new-wall-no-btn">
            <Trans i18nKey="addNewWall.noOtherWalls" />
          </Button>

          <Button type="button" variant="primary" onClick={onAddAnotherWall} data-testid="add-new-wall-yes-btn">
            <Trans i18nKey="addNewWall.haveAnotherWall" />
          </Button>
        </div>
      }
    />
  )
}
